// Local state container. The single mutable `local` plus a subscriber
// list; UI re-renders by subscribing to changes. Ops apply optimistically
// through `localApply`, which also forwards them to the transport when
// one is attached. Echoes from the server land in `applyServerMsg`.

import { BUILD_ID } from "./build_info";
import type { ClientInfoPublic } from "./generated/ClientInfoPublic";
import type { CursorPos } from "./generated/CursorPos";
import type { MatrixState } from "./generated/MatrixState";
import type { Op } from "./generated/Op";
import type { Quadrant } from "./generated/Quadrant";
import type { SeqOp } from "./generated/SeqOp";
import type { ServerMsg } from "./generated/ServerMsg";
import type { Task } from "./generated/Task";
import { applyOp } from "./shared/ops";
import { generateClientOpId } from "./shared/ids";
import type { Transport } from "./transport";

const STORED_NAME_KEY = "eyes_user_name";

function readStoredName(): string | null {
  try {
    return localStorage.getItem(STORED_NAME_KEY);
  } catch {
    return null;
  }
}

function writeStoredName(name: string): void {
  try {
    localStorage.setItem(STORED_NAME_KEY, name);
  } catch {
    // localStorage may be disabled in private mode; just skip.
  }
}

export interface LocalState {
  state: MatrixState;
  /** Last `seq` we've applied. Used to ask the server for a delta replay. */
  seq: number;
  /** Ops applied locally but not yet echoed back from the server. */
  pendingOps: Array<{ op: Op; clientOpId: string; baseSeq: number }>;
  /** Other connected clients (and self), keyed by clientId. */
  presence: Map<string, ClientInfoPublic>;
  me: ClientInfoPublic | null;
  connection: "connecting" | "connected" | "reconnecting" | "offline";
  /** True when the server reported a different build id than this bundle was
   *  compiled with. Triggers the "newer version available" toast. */
  outOfDate: boolean;
  /** Free-text + tag filter string, e.g. `tag:travel hotel`. Purely
   *  view state — not synced to peers; mirrored to the URL hash. */
  query: string;
}

/** Notification kinds:
 *  - "full": something the matrix / roster / status indicator cares about
 *    changed. Subscribers should re-render their view.
 *  - "cursor-only": only a remote client's cursor moved. Cursor layer
 *    updates directly; everything else can skip its render path.
 */
export type NotifyKind = "full" | "cursor-only";
type Listener = (kind: NotifyKind) => void;

const listeners = new Set<Listener>();

export const local: LocalState = {
  state: {
    v: 1,
    tasks: {},
    settings: { imminentDays: 2, soonDays: 7, showCompleted: false },
  },
  seq: 0,
  pendingOps: [],
  presence: new Map(),
  me: null,
  connection: "connecting",
  outOfDate: false,
  query: "",
};

let transport: Transport | null = null;

export function attachTransport(t: Transport): void {
  transport = t;
}

export function detachTransport(): void {
  transport = null;
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notify(kind: NotifyKind = "full"): void {
  for (const fn of listeners) fn(kind);
}

/** Apply an op locally, queue it as pending, and ship it to the server. */
export function localApply(op: Op): void {
  applyOp(local.state, op);
  const clientOpId = generateClientOpId();
  local.pendingOps.push({ op, clientOpId, baseSeq: local.seq });
  transport?.sendOp(op, clientOpId, local.seq);
  notify();
}

/** Apply an inbound server message. */
export function applyServerMsg(msg: ServerMsg): void {
  switch (msg.kind) {
    case "snapshot":
      local.state = msg.state;
      local.seq = msg.seq;
      local.me = msg.you;
      local.presence = new Map(msg.clients.map((c) => [c.clientId, c]));
      // Re-apply optimistic ops on top of the snapshot so the local view
      // doesn't visibly snap back to server state while pending ops are
      // still in flight.
      for (const p of local.pendingOps) applyOp(local.state, p.op);
      if (msg.serverBuildId !== BUILD_ID) local.outOfDate = true;
      reapplyStoredName();
      break;
    case "replay":
      // Apply missing remote ops (skipping any whose clientOpId matches
      // a pending op — those are echoes of our own work, already applied).
      for (const so of msg.ops) {
        if (!removePending(so)) applyOp(local.state, so.op);
      }
      local.seq = msg.seq;
      local.me = msg.you;
      local.presence = new Map(msg.clients.map((c) => [c.clientId, c]));
      if (msg.serverBuildId !== BUILD_ID) local.outOfDate = true;
      reapplyStoredName();
      break;
    case "op": {
      const wasOurs = msg.clientOpId !== null && removePendingId(msg.clientOpId);
      if (!wasOurs) applyOp(local.state, msg.op);
      local.seq = msg.seq;
      break;
    }
    case "reject":
      // Drop the rejected op and ask the transport to resync. The simplest
      // correct path per design §9.3: trigger a full snapshot via the
      // server's existing flow. We just drop the pending entry; on the
      // next hello-echo cycle the state will reconcile.
      removePendingId(msg.clientOpId);
      console.warn("op rejected:", msg.code, msg.message);
      break;
    case "presence-join":
      local.presence.set(msg.client.clientId, msg.client);
      break;
    case "presence-leave":
      local.presence.delete(msg.clientId);
      break;
    case "presence-update": {
      const prev = local.presence.get(msg.clientId);
      local.presence.set(msg.clientId, msg.patch);
      if (local.me && local.me.clientId === msg.clientId) {
        local.me = msg.patch;
      }
      // Cursor-only updates skip the heavy re-render. Other fields
      // (name, editing) need it.
      if (
        prev !== undefined &&
        prev.name === msg.patch.name &&
        prev.color === msg.patch.color &&
        prev.editing === msg.patch.editing
      ) {
        notify("cursor-only");
        return;
      }
      break;
    }
  }
  notify();
}

export function setConnection(c: LocalState["connection"]): void {
  if (local.connection === c) return;
  local.connection = c;
  notify();
}

export function lastSeq(): number {
  return local.seq;
}

/** Seed `local.state` and `local.seq` from a cached snapshot before the
 *  transport opens, so the first render paints against real tasks instead
 *  of an empty board. No-op once any server message has landed — the
 *  server is always authoritative and must never be clobbered by cache. */
export function hydrateFromCache(state: MatrixState, seq: number): void {
  if (local.seq > 0) return;
  local.state = state;
  local.seq = seq;
}

export function pendingOpsForTransport(): Array<{
  op: Op;
  clientOpId: string;
  baseSeq: number;
}> {
  return local.pendingOps.slice();
}

/** Pre-sorted tasks for a quadrant. */
export function tasksInQuadrant(q: Quadrant): Array<[string, Task]> {
  const entries: Array<[string, Task]> = [];
  for (const [id, t] of Object.entries(local.state.tasks)) {
    if (t && t.quadrant === q) entries.push([id, t]);
  }
  entries.sort((a, b) =>
    a[1].rank < b[1].rank ? -1 : a[1].rank > b[1].rank ? 1 : 0,
  );
  return entries;
}

/** Global 1-based priority numbering, Q1 → Q2 → Q3 → Q4 top-to-bottom.
 *  Completed tasks are skipped — they don't have a priority position
 *  (the row shows a checkmark instead). */
export function priorityNumber(taskId: string): number | null {
  const target = local.state.tasks[taskId];
  if (!target || target.completedAt) return null;
  let n = 1;
  for (const q of ["q1", "q2", "q3", "q4"] as const) {
    for (const [id, task] of tasksInQuadrant(q)) {
      if (task.completedAt) continue;
      if (id === taskId) return n;
      n++;
    }
  }
  return null;
}

/** Drop a pending op whose seq+op match `so`. Returns whether one was found. */
function removePending(so: SeqOp): boolean {
  const idx = local.pendingOps.findIndex((p) =>
    opsEqualShape(p.op, so.op),
  );
  if (idx === -1) return false;
  local.pendingOps.splice(idx, 1);
  return true;
}

function removePendingId(clientOpId: string): boolean {
  const idx = local.pendingOps.findIndex((p) => p.clientOpId === clientOpId);
  if (idx === -1) return false;
  local.pendingOps.splice(idx, 1);
  return true;
}

/** Cheap equality on op shape — used as a tiebreaker in replay when the
 *  server doesn't echo clientOpId. Two ops with identical payload coming
 *  from a replay are treated as "ours" if we have a matching pending. */
function opsEqualShape(a: Op, b: Op): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Convenience for the input layer to send presence updates. */
export function sendCursor(pos: CursorPos | null): void {
  transport?.sendCursor(pos);
}

export function sendEditing(taskId: string | null): void {
  transport?.sendEditing(taskId);
}

export function sendIdentify(name: string): void {
  writeStoredName(name);
  transport?.sendIdentify(name);
  if (local.me) {
    local.me = { ...local.me, name };
    local.presence.set(local.me.clientId, local.me);
    notify();
  }
}

/** After a (re)connect, push the stored display name back to the server
 *  so this client's identity survives reconnects with a fresh `clientId`. */
function reapplyStoredName(): void {
  const stored = readStoredName();
  if (!stored) return;
  if (local.me && local.me.name === stored) return;
  transport?.sendIdentify(stored);
  if (local.me) {
    local.me = { ...local.me, name: stored };
    local.presence.set(local.me.clientId, local.me);
  }
}
